import type { Food, FoodBasis, FoodPortion } from "../foods/food";
import {
  FOOD_ID_MAX_LENGTH,
  FOOD_NAME_MAX_LENGTH,
  FOOD_SERVING_NAME_MAX_LENGTH,
  nutrientsForPortion,
  validateFood,
  validateFoodPortion,
} from "../foods/food";
import type { NutrientProfile } from "../nutrition/nutrients";
import { addNutrients, EMPTY_NUTRIENTS, scaleNutrients } from "../nutrition/nutrients";
import { requireFinitePositive } from "../shared/validation";

export const RECIPE_ID_MAX_LENGTH = FOOD_ID_MAX_LENGTH;
export const RECIPE_NAME_MAX_LENGTH = FOOD_NAME_MAX_LENGTH;
export const RECIPE_SERVING_NAME_MAX_LENGTH = FOOD_SERVING_NAME_MAX_LENGTH;
export const DEFAULT_RECIPE_SERVING_NAME = "serving";

/** A copy of the food data used when a recipe is saved. */
export interface RecipeIngredient {
  readonly foodId: string;
  readonly name: string;
  readonly brand?: string;
  readonly basis: FoodBasis;
  readonly nutrients: NutrientProfile;
  readonly portion: FoodPortion;
}

export interface Recipe {
  readonly id: string;
  readonly name: string;
  readonly servingName: string;
  /** Number of servings produced by the full collection of ingredients. */
  readonly servingCount: number;
  readonly ingredients: readonly RecipeIngredient[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export function snapshotRecipeIngredient(food: Food, portion: FoodPortion): RecipeIngredient {
  const validatedFood = validateFood(food);
  const validatedPortion = validateFoodPortion(portion, validatedFood.basis);
  return {
    foodId: validatedFood.id,
    name: validatedFood.name,
    brand: validatedFood.brand,
    basis: { ...validatedFood.basis },
    nutrients: cloneNutrients(validatedFood.nutrients),
    portion: { ...validatedPortion },
  };
}

export function validateRecipeIngredient(ingredient: RecipeIngredient): RecipeIngredient {
  if (ingredient === null || typeof ingredient !== "object") {
    throw new Error("Recipe ingredient is required");
  }
  const food = validateFood({
    id: ingredient.foodId,
    name: ingredient.name,
    brand: ingredient.brand,
    basis: ingredient.basis,
    nutrients: ingredient.nutrients,
    provenance: { kind: "custom" },
    preferred: true,
    createdAt: "recipe-snapshot",
    updatedAt: "recipe-snapshot",
  });
  const portion = validateFoodPortion(ingredient.portion, food.basis);
  return {
    foodId: food.id,
    name: food.name,
    brand: food.brand,
    basis: { ...food.basis },
    nutrients: cloneNutrients(food.nutrients),
    portion: { ...portion },
  };
}

export function validateRecipe(recipe: Recipe): Recipe {
  if (recipe === null || typeof recipe !== "object") throw new Error("Recipe is required");
  const id = requiredRecipeText(recipe.id, "Recipe id", RECIPE_ID_MAX_LENGTH);
  const name = requiredRecipeText(recipe.name, "Recipe name", RECIPE_NAME_MAX_LENGTH);
  const servingName = optionalRecipeText(
    recipe.servingName,
    "Recipe serving name",
    RECIPE_SERVING_NAME_MAX_LENGTH,
  ) ?? DEFAULT_RECIPE_SERVING_NAME;
  requireFinitePositive(recipe.servingCount, "Recipe serving count");
  if (!Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) {
    throw new Error("A recipe requires at least one ingredient");
  }
  return {
    ...recipe,
    id,
    name,
    servingName,
    servingCount: recipe.servingCount,
    ingredients: recipe.ingredients.map(validateRecipeIngredient),
  };
}

/** Nutrients in the complete batch before it is split into servings. */
export function totalRecipeNutrients(recipe: Recipe): NutrientProfile {
  return totalNutrientsForValidatedRecipe(validateRecipe(recipe));
}

/** Nutrients in exactly one of the recipe's declared servings. */
export function perServingRecipeNutrients(recipe: Recipe): NutrientProfile {
  const validated = validateRecipe(recipe);
  return scaleNutrients(totalNutrientsForValidatedRecipe(validated), 1 / validated.servingCount);
}

/** Presents one recipe serving to the existing diary snapshot machinery. */
export function recipeAsFood(recipe: Recipe): Food {
  const validated = validateRecipe(recipe);
  return validateFood({
    id: validated.id,
    name: validated.name,
    basis: { amount: 1, unit: "serving", servingName: validated.servingName },
    nutrients: scaleNutrients(totalNutrientsForValidatedRecipe(validated), 1 / validated.servingCount),
    provenance: { kind: "custom" },
    preferred: true,
    createdAt: validated.createdAt,
    updatedAt: validated.updatedAt,
  });
}

function totalNutrientsForValidatedRecipe(recipe: Recipe): NutrientProfile {
  return recipe.ingredients.reduce((total, ingredient) => {
    const food: Food = {
      id: ingredient.foodId,
      name: ingredient.name,
      brand: ingredient.brand,
      basis: ingredient.basis,
      nutrients: ingredient.nutrients,
      provenance: { kind: "custom" },
      preferred: true,
      createdAt: recipe.createdAt,
      updatedAt: recipe.updatedAt,
    };
    return addNutrients(total, nutrientsForPortion(food, ingredient.portion));
  }, EMPTY_NUTRIENTS);
}

function cloneNutrients(nutrients: NutrientProfile): NutrientProfile {
  return {
    macros: { ...nutrients.macros },
    fibreGrams: nutrients.fibreGrams,
    micronutrients: Object.fromEntries(
      Object.entries(nutrients.micronutrients).map(([key, value]) => [key, { ...value }]),
    ),
  };
}

function requiredRecipeText(value: string, label: string, maximumLength: number): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${label} cannot be empty`);
  const trimmed = value.trim();
  if (trimmed.length > maximumLength) throw new Error(`${label} cannot exceed ${maximumLength} characters`);
  return trimmed;
}

function optionalRecipeText(value: string | undefined, label: string, maximumLength: number): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new Error(`${label} must be text`);
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > maximumLength) throw new Error(`${label} cannot exceed ${maximumLength} characters`);
  return trimmed;
}
